













































































import useBlob from "@/hooks/useBlob";
import MNotificationVue from "@/mixins/MNotification.vue";
import {
  ALLOWED_IMAGE_FILE,
  ALLOWED_PDF_FILE,
} from "@/models/constants/upload.constant";
import { ContractUploaderListDto } from "@/models/interface/contract-uploader/ContractUploaderListDto.interface";
import { contractUploaderService } from "@/services/ContractUploader.service";
import printJs from "print-js";
import pdf from "vue-pdf";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";

const MIME_TYPE_MAP: Record<string, string> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
};

const ZOOM_LEVEL_STEP = 0.2;

@Component({
  components: {
    pdf,
  },
})
export default class ContractPreviewModal extends Mixins(MNotificationVue) {
  @Prop({ default: false }) visible!: boolean;
  @Prop({ default: null }) record!: ContractUploaderListDto | null;

  loading = false;
  previewUrl = "";
  fileType = "";
  zoomLevel = 1;
  pdfSrc: string | typeof pdf | null = null;
  numPages = 0;
  isPdfLoaded = false;

  get modalTitle(): string {
    if (!this.record) return "";
    return `${this.record.documentNo} - ${this.record.customerName}`;
  }

  @Watch("record")
  onRecordChanged(newRecord: ContractUploaderListDto | null): void {
    if (newRecord?.fileContract) {
      this.loadFile(newRecord);
    }
  }

  @Watch("visible")
  onVisibleChanged(newVal: boolean): void {
    if (!newVal) {
      // Cleanup when modal is closed
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = "";
      }
      this.pdfSrc = null;
      this.numPages = 0;
      this.isPdfLoaded = false;
    }
  }

  get isImage(): boolean {
    return ALLOWED_IMAGE_FILE.includes(`.${this.fileType}`);
  }

  loadFile(record: ContractUploaderListDto): void {
    if (!record.fileContract) return;

    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }

    this.zoomLevel = 1;
    this.loading = true;
    this.previewUrl = "";
    this.numPages = 0;
    this.isPdfLoaded = false;

    this.fileType = this.getFileType(record);

    contractUploaderService
      .getFile(record.fileContract)
      .then(blob => {
        const mimeType =
          MIME_TYPE_MAP[this.fileType] ?? "application/octet-stream";
        const typedBlob = new Blob([blob], { type: mimeType });

        this.previewUrl = URL.createObjectURL(typedBlob);

        if (this.fileType === "pdf") {
          this.pdfSrc = pdf.createLoadingTask(this.previewUrl);

          this.pdfSrc.promise
            .then((pdfDocument: { numPages: number }) => {
              this.numPages = pdfDocument.numPages;
            })
            .catch(() => {
              this.showNotifError("notif_preview_failed");
            });
        }
      })
      .catch(() => {
        this.showNotifError("notif_preview_failed");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  getFileType(record: ContractUploaderListDto): string {
    if (!record.fileContract) return "pdf";

    const fileName = record.fileContract.toLowerCase();

    if (ALLOWED_PDF_FILE.some(ext => fileName.endsWith(ext))) {
      return "pdf";
    }

    if (ALLOWED_IMAGE_FILE.some(ext => fileName.endsWith(ext))) {
      const ext = ALLOWED_IMAGE_FILE.find(ext => fileName.endsWith(ext));
      return ext?.substring(1) || "jpg";
    }

    return "pdf";
  }

  handleDownload(): void {
    if (this.record === null) return;
    if (this.record.fileContract == null) return;

    const fileContract = this.record.fileContract;
    const fileName = fileContract.split("/").pop();

    if (fileName == null) return;

    contractUploaderService
      .getFile(fileContract)
      .then(blob => {
        const { toDownload } = useBlob();
        toDownload(blob, fileName);
      })
      .catch(() => {
        this.showNotifError("notif_error_status_code");
      });
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + ZOOM_LEVEL_STEP, 3);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - ZOOM_LEVEL_STEP, 0.5);
  }

  handlePdfLoaded(): void {
    this.isPdfLoaded = true;
  }

  handleNumPages(num: number): void {
    this.numPages = num;
  }

  handlePdfError(): void {
    this.isPdfLoaded = false;
    this.showNotifError("notif_preview_failed");
  }

  handlePrint(): void {
    if (!this.previewUrl) return;
    printJs(this.previewUrl);
  }

  handleClose(): void {
    this.$emit("close");
  }

  beforeDestroy(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = "";
    }
  }
}
